import React, { Fragment } from "react";
import PropTypes from "prop-types";
import {
  useTable,
  useGlobalFilter,
  useAsyncDebounce,
  useSortBy,
  useFilters,
  useExpanded,
  usePagination,
} from "react-table";
import { Table, Row, Col, Button, Input } from "reactstrap";
import { Filter, DefaultColumnFilter } from "./filters";
import JobListGlobalFilter from "../../components/Common/GlobalSearchFilter";
import { useTranslation } from "react-i18next";

// Define a default UI for filtering
function GlobalFilter({
  preGlobalFilteredRows,
  globalFilter,
  setGlobalFilter,
  isJobListGlobalFilter
}) {
  const count = preGlobalFilteredRows.length;
  const [value, setValue] = React.useState(globalFilter);
  const onChange = useAsyncDebounce(value => {
    setGlobalFilter(value || undefined);
  }, 200);

  return (
    <React.Fragment>
      <Col md={4}>
        <div className="search-box me-xxl-2 my-3 my-xxl-0 d-inline-block">
          <div className="position-relative">
            <label htmlFor="search-bar-0" className="search-label">
              <span id="search-bar-0-label" className="sr-only">
                Search this table
              </span>
              <input
                onChange={e => {
                  setValue(e.target.value);
                  onChange(e.target.value);
                }}
                id="search-bar-0"
                type="text"
                className="form-control"
                placeholder={`${count} records...`}
                value={value || ""}
              />
            </label>
            <i className="bx bx-search-alt search-icon"></i>
          </div>
        </div>
      </Col>
      {isJobListGlobalFilter && (
        <JobListGlobalFilter />
      )}
    </React.Fragment>
  );
};

const TableContainer = ({
  columns,
  data,
  isGlobalFilter,
  isJobListGlobalFilter,
  isAddOptions,
  canUpload,
  isAddUserList,
  handleOrderClicks,
  handleUserClick,
  handleStationClick,
  isAddStationList,
  customPageSize,
  className,
  customPageSizeOptions,
  handleClientClick,
  isAddClientList,
  handleVehiculeClick,
  handleEmployeClick,
  isAddVehiculeList,
  isAddDeptList,
  handleDownloadTemp,
  handleUpload,
  handleLocaliteClick,
  isAddLocaliteList,
  handleCategorieClick,
  isAddCategorieList,
  isAddEquipementList,
  handleEquipementClick,
  isAddPanneList,
  handlePanneClick,
  isAddParamList,
  withPages
}) => {

  const { t } = useTranslation('translation');

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      defaultColumn: { Filter: DefaultColumnFilter },
      initialState: {
        pageIndex: 0,
        pageSize: customPageSize,
        sortBy: [
          {
            desc: true,
          },
        ],
      },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination
  );

  const generateSortingIndicator = column => {
    return column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : "";
  };

  const onChangeInSelect = event => {
    setPageSize(Number(event.target.value));
  };

  const onChangeInInput = event => {
    const page = event.target.value ? Number(event.target.value) - 1 : 0;
    gotoPage(page);
  };

  return (
    <Fragment>
      <Row className="mb-2">
        {withPages && <Col md={customPageSizeOptions ? 2 : 1}>
          <select
            className="form-select"
            value={pageSize}
            onChange={onChangeInSelect}
          >
            {[10, 20, 30, 40, 50].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </Col>}
        {isGlobalFilter && (
          <GlobalFilter
            preGlobalFilteredRows={preGlobalFilteredRows}
            globalFilter={state.globalFilter}
            setGlobalFilter={setGlobalFilter}
            isJobListGlobalFilter={isJobListGlobalFilter}
          />
        )}
        {canUpload && <Col  sm="7">
          <div className="input-group">
            <input accept="application/vnd.ms-excelapplication/vnd.openxmlformats-officedocument.spreadsheetml.sheet" type="file" className="form-control" id="customFile" onChange={handleUpload}/>
            
          </div>
        </Col>}
        {isAddOptions && (
          <Col sm="7">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                className="btn-rounded  mb-2 me-2"
                onClick={handleOrderClicks}
              >
                <i className="mdi mdi-plus me-1" />
                Add New Order
              </Button>
            </div>
          </Col>
        )}

        {isAddClientList && (
          <Col sm="3">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                size="sm"
                className="btn-rounded mb-2 me-2"
                onClick={handleClientClick}
              >
                <i className="mdi mdi-plus me-1" />
                {t('actions.new')}
              </Button>
              {/* <Button
                type="button"
                size="sm"
                color="primary"
                className="btn-rounded mb-2 me-2"
                onClick={handleDownloadTemp}
              >
                <i className="mdi mdi-download me-1" />
                {t('actions.downloadTemp')}
              </Button> */}

            </div>
          </Col>
        )}
        {isAddVehiculeList && (
          <Col sm="3">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                size="sm"
                className="btn-rounded mb-2 me-2"
                onClick={handleVehiculeClick}
              >
                <i className="mdi mdi-plus me-1" />
                {t('actions.new')}
              </Button>
              {/* <Button
                type="button"
                size="sm"
                color="primary"
                className="btn-rounded mb-2 me-2"
                onClick={handleDownloadTemp}
              >
                <i className="mdi mdi-download me-1" />
                {t('actions.downloadTemp')}
              </Button> */}
            </div>
          </Col>
        )}
        {isAddPanneList && (
          <Col sm="3">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                size="sm"
                className="btn-rounded mb-2 me-2"
                onClick={handlePanneClick}
              >
                <i className="mdi mdi-plus me-1" />
                {t('actions.new')}
              </Button>
              <Button
                type="button"
                size="sm"
                color="primary"
                className="btn-rounded mb-2 me-2"
                onClick={handleDownloadTemp}
              >
                <i className="mdi mdi-download me-1" />
                {t('actions.downloadTemp')}
              </Button>
            </div>
          </Col>
        )}
        {isAddEquipementList && (
          <Col sm="3">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                size="sm"
                className="btn-rounded mb-2 me-2"
                onClick={handleEquipementClick}
              >
                <i className="mdi mdi-plus me-1" />
                {t('actions.new')}
              </Button>
              <Button
                type="button"
                size="sm"
                color="primary"
                className="btn-rounded mb-2 me-2"
                onClick={handleDownloadTemp}
              >
                <i className="mdi mdi-download me-1" />
                {t('actions.downloadTemp')}
              </Button>
            </div>
          </Col>
        )}
        {isAddParamList && (
          <Col sm="3">
            <div className="text-sm-end">
            </div>
          </Col>
        )}
        {isAddCategorieList && (
          <Col sm="3">
            <div className="text-sm-end">
              <Button
                type="button"
                color="success"
                size="sm"
                className="btn-rounded mb-2 me-2"
                onClick={handleCategorieClick}
              >
                <i className="mdi mdi-plus me-1" />
                {t('actions.new')}
              </Button>
              <Button
                type="button"
                size="sm"
                color="primary"
                className="btn-rounded mb-2 me-2"
                onClick={handleDownloadTemp}
              >
                <i className="mdi mdi-download me-1" />
                {t('actions.downloadTemp')}
              </Button>
            </div>
          </Col>
        )}



        {/* {canDownloadtemp && (
          <Col sm="3">
            <div className="text-sm-end">
              <Button
                type="button"
                color="primary"
                className="btn-rounded mb-2 me-2"
                onClick={handleDownloadTemp}
              >
                <i className="mdi mdi-download me-1" />
                {t('actions.downloadTemp')}
              </Button>
            </div>
          </Col>
        )} */}
      </Row>

      <div className="table-responsive react-table">
        <Table bordered hover {...getTableProps()} className={className}>
          <thead className="table- table-nowrap">
            {headerGroups.map(headerGroup => (
              <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th key={column.id}>
                    <div className="mb-2" {...column.getSortByToggleProps()}>
                      {column.render("Header")}
                      {generateSortingIndicator(column)}
                    </div>
                    <Filter column={column} />
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.map(row => {
              prepareRow(row);
              return (
                <Fragment key={row.getRowProps().key}>
                  <tr>
                    {row.cells.map(cell => {
                      return (
                        <td key={cell.id} {...cell.getCellProps()}>
                          <small>{cell.render("Cell")}</small>
                        </td>
                      );
                    })}
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </Table>
      </div>
    </Fragment>
  );
};

TableContainer.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
};

export default TableContainer;
